import { useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { FileText, Upload, Download, Trash2, Edit, FileCode, Loader2, Wand2 } from 'lucide-react';
import AITemplateConverter from '../AITemplateConverter';
import { API_URL, categoryColors, formatFileSize } from './templateConstants';

export default function DocumentTemplatesTab({ 
  token, 
  templates, 
  onRefresh 
}) {
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadData, setUploadData] = useState({
    name: '',
    description: '',
    category: 'templates',
    case_types: ''
  });
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editData, setEditData] = useState({
    name: '',
    description: '',
    category: '',
    case_types: ''
  });
  const [saving, setSaving] = useState(false);
  const [creatingSamples, setCreatingSamples] = useState(false);

  const handleUpload = async () => {
    if (!uploadFile || !uploadData.name) {
      toast.error('Please provide a name and select a file');
      return;
    }

    if (!uploadFile.name.endsWith('.docx')) {
      toast.error('Only .docx files are supported');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('name', uploadData.name);
      if (uploadData.description) formData.append('description', uploadData.description);
      formData.append('category', uploadData.category);
      if (uploadData.case_types) formData.append('case_types', uploadData.case_types);

      const res = await fetch(`${API_URL}/api/case-management/document-templates`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (res.ok) {
        toast.success('Template uploaded successfully');
        setShowUploadDialog(false);
        setUploadData({ name: '', description: '', category: 'templates', case_types: '' });
        setUploadFile(null);
        onRefresh();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to upload template');
      }
    } catch (error) {
      toast.error('Failed to upload template');
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = async () => {
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('name', editData.name);
      if (editData.description !== undefined) formData.append('description', editData.description);
      formData.append('category', editData.category);
      if (editData.case_types !== undefined) formData.append('case_types', editData.case_types);

      const res = await fetch(`${API_URL}/api/case-management/document-templates/${editingTemplate.id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (res.ok) {
        toast.success('Template updated');
        setShowEditDialog(false);
        setEditingTemplate(null);
        onRefresh();
      } else {
        toast.error('Failed to update template');
      }
    } catch (error) {
      toast.error('Failed to update template');
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async (templateId, filename) => {
    try {
      const res = await fetch(`${API_URL}/api/case-management/document-templates/${templateId}/download`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleDelete = async (templateId) => {
    if (!confirm('Are you sure you want to delete this template?')) return;

    try {
      const res = await fetch(`${API_URL}/api/case-management/document-templates/${templateId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Template deleted');
        onRefresh();
      } else {
        toast.error('Failed to delete template');
      }
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const handleCreateSamples = async () => {
    setCreatingSamples(true);
    try {
      const res = await fetch(`${API_URL}/api/case-management/document-templates/create-samples`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        if (data.templates?.length > 0) {
          toast.success(`Created ${data.templates.length} sample templates!`);
          onRefresh();
        } else {
          toast.info('Sample templates already exist');
        }
      } else {
        toast.error('Failed to create sample templates');
      }
    } catch (error) {
      toast.error('Failed to create sample templates');
    } finally {
      setCreatingSamples(false);
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Templates</CardTitle>
              <CardDescription>
                Upload .docx files with merge fields like {'{{client_name}}'} to auto-fill case data
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline"
                onClick={handleCreateSamples}
                disabled={creatingSamples}
                data-testid="create-samples-btn"
              >
                {creatingSamples ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Wand2 className="w-4 h-4 mr-2" />
                )}
                Add Samples
              </Button>
              <AITemplateConverter onTemplateCreated={onRefresh} />
              <Button 
                onClick={() => setShowUploadDialog(true)}
                data-testid="upload-template-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload Template
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {templates.length > 0 ? (
            <div className="space-y-3">
              {templates.map((template) => (
                <div
                  key={template.id}
                  className="flex items-center justify-between p-4 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                  data-testid={`template-item-${template.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{template.name}</p>
                        {template.is_sample && (
                          <Badge variant="outline" className="text-xs">Sample</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge className={categoryColors[template.category] || categoryColors.other}>
                          {template.category}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {formatFileSize(template.file_size)}
                        </span>
                        {template.merge_fields?.length > 0 && (
                          <span className="text-xs text-muted-foreground">
                            • {template.merge_fields.length} fields
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="icon" onClick={() => handleDownload(template.id, template.original_filename)}>
                      <Download className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setEditingTemplate(template);
                        setEditData({
                          name: template.name,
                          description: template.description || '',
                          category: template.category,
                          case_types: template.case_types?.join(', ') || ''
                        });
                        setShowEditDialog(true);
                      }}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(template.id)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <FileCode className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No Document Templates</h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Upload .docx templates with merge fields to generate documents filled with case data.
              </p>
              <div className="flex items-center justify-center gap-3">
                <Button variant="outline" onClick={handleCreateSamples} disabled={creatingSamples}>
                  <Wand2 className="w-4 h-4 mr-2" />
                  Add Samples
                </Button>
                <Button onClick={() => setShowUploadDialog(true)}>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Template
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Document Template</DialogTitle>
            <DialogDescription>
              Upload a .docx file with merge fields like {'{{client_name}}'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Template Name *</Label>
              <Input
                value={uploadData.name}
                onChange={(e) => setUploadData({ ...uploadData, name: e.target.value })}
                placeholder="e.g., Client Retainer Agreement"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={uploadData.description}
                onChange={(e) => setUploadData({ ...uploadData, description: e.target.value })}
                placeholder="Brief description of this template"
              />
            </div>
            <div className="space-y-2">
              <Label>Category</Label>
              <Select
                value={uploadData.category}
                onValueChange={(value) => setUploadData({ ...uploadData, category: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pleadings">Pleadings</SelectItem>
                  <SelectItem value="discovery">Discovery</SelectItem>
                  <SelectItem value="correspondence">Correspondence</SelectItem>
                  <SelectItem value="contracts">Contracts</SelectItem>
                  <SelectItem value="templates">Templates</SelectItem>
                  <SelectItem value="internal">Internal</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Case Types (comma-separated)</Label>
              <Input
                value={uploadData.case_types}
                onChange={(e) => setUploadData({ ...uploadData, case_types: e.target.value })}
                placeholder="e.g., personal_injury, family_law"
              />
            </div>
            <div className="space-y-2">
              <Label>File (.docx) *</Label>
              <Input
                type="file"
                accept=".docx"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpload} disabled={uploading}>
              {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Template Name</Label>
              <Input
                value={editData.name}
                onChange={(e) => setEditData({ ...editData, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={editData.description}
                onChange={(e) => setEditData({ ...editData, description: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Category</Label>
              <Select
                value={editData.category}
                onValueChange={(value) => setEditData({ ...editData, category: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pleadings">Pleadings</SelectItem>
                  <SelectItem value="discovery">Discovery</SelectItem>
                  <SelectItem value="correspondence">Correspondence</SelectItem>
                  <SelectItem value="contracts">Contracts</SelectItem>
                  <SelectItem value="templates">Templates</SelectItem>
                  <SelectItem value="internal">Internal</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Case Types (comma-separated)</Label>
              <Input
                value={editData.case_types}
                onChange={(e) => setEditData({ ...editData, case_types: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleEdit} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
